import { useState, useEffect } from 'react'
import { useLogin } from '@refinedev/core'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Eye, EyeOff, Lock, User } from 'lucide-react'
import fullLogo from '@/assets/logos/logo-landscape.png'
import { flaskApi } from '@/lib/flaskApi'

export function LoginPage() {
  const { mutate: login, isLoading } = useLogin()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw] = useState(false)

  useEffect(() => {
    flaskApi.get('/auth/csrf-token').catch(() => {})
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    login({ username, password })
  }

  return (
    <div className="min-h-screen flex overflow-hidden bg-[#e8f4fb] dark:bg-[#071e30]">

      <div className="relative flex flex-1 flex-col items-center justify-center px-8 py-12">

        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#076698]/10 blur-[100px]" />
          <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-sky-400/15 blur-3xl" />
        </div>

        <div className="relative flex flex-col items-center mb-10 select-none">
          <img
            src={fullLogo}
            alt="Talentino"
            className="h-16 object-contain mb-2 
                       dark:[filter:invert(70%)_sepia(60%)_saturate(400%)_hue-rotate(175deg)_brightness(110%)]"
          />
          <span className="text-[10px] font-extrabold uppercase tracking-[0.28em] text-[#076698] dark:text-sky-400">
            Admin Studio
          </span>
        </div>

        <div className="relative w-full max-w-sm
                        bg-white/80 dark:bg-slate-800/70
                        backdrop-blur-2xl
                        border border-white/90 dark:border-white/10
                        rounded-3xl
                        shadow-[0_24px_64px_rgba(7,102,152,0.12)]
                        p-8">

          <div className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-inset ring-white/60 dark:ring-white/8" />

          <h2 className="text-[18px] font-extrabold tracking-tight text-slate-800 dark:text-slate-50 mb-1">
            Welcome back
          </h2>
          <p className="text-xs text-slate-400 dark:text-slate-400 mb-7">
            Sign in to continue to your admin account
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-[11px] font-extrabold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                Username
              </label>
              <div className="relative">
                <User size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                <Input
                  className="pl-9 bg-slate-50/80 dark:bg-slate-700/60 border-slate-200 dark:border-slate-600
                             text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500
                             focus-visible:ring-[#076698]/40 focus-visible:border-[#076698]/60 rounded-xl"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoComplete="username"
                  autoFocus
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-extrabold uppercase tracking-widest text-slate-500 dark:text-slate-400">
                Password
              </label>
              <div className="relative">
                <Lock size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                <Input
                  type={showPw ? 'text' : 'password'}
                  className="pl-9 pr-10 bg-slate-50/80 dark:bg-slate-700/60 border-slate-200 dark:border-slate-600
                             text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500
                             focus-visible:ring-[#076698]/40 focus-visible:border-[#076698]/60 rounded-xl"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                >
                  {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full mt-2 h-10 rounded-xl bg-[#076698] hover:bg-[#055580] active:bg-[#044268]
                         text-white font-bold shadow-md shadow-[#076698]/20 transition-all duration-150"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  Signing in…
                </span>
              ) : 'Sign in to Dashboard'}
            </Button>
          </form>

          <p className="text-center text-[10px] text-slate-400 dark:text-slate-600 mt-6">
            © {new Date().getFullYear()} Talentino. All rights reserved.
          </p>
        </div>
      </div>

      <div className="hidden lg:flex relative w-[42%] flex-col justify-end overflow-hidden
                      bg-gradient-to-br from-[#f0f8fd] via-[#e0f0f9] to-[#cde6f5]
                      dark:from-slate-800 dark:via-[#0c2a3e] dark:to-[#071e30]">

        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-[-10%] right-[-15%] w-[500px] h-[500px] rounded-full
                          bg-white/40 dark:bg-sky-400/5 blur-2xl" />
          <div className="absolute top-[20%] right-[10%] w-64 h-64 rounded-full
                          bg-[#076698]/8 dark:bg-sky-500/10 blur-3xl" />
          <div className="absolute bottom-[-10%] left-[-5%] w-80 h-80 rounded-full
                          bg-[#076698]/6 dark:bg-sky-600/8 blur-3xl" />
        </div>

        <div className="pointer-events-none absolute inset-0 opacity-25 dark:opacity-10">
          <svg viewBox="0 0 500 700" fill="none" className="w-full h-full">
            <ellipse cx="380" cy="160" rx="200" ry="280" transform="rotate(-25 380 160)" fill="white" />
            <ellipse cx="420" cy="200" rx="140" ry="220" transform="rotate(-20 420 200)"
              fill="url(#lGrad)" />
            <defs>
              <radialGradient id="lGrad" cx="50%" cy="30%" r="70%">
                <stop offset="0%" stopColor="#076698" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#076698" stopOpacity="0.05" />
              </radialGradient>
            </defs>
          </svg>
        </div>

        <div className="absolute left-0 top-1/3 w-px h-24 bg-gradient-to-b from-transparent via-[#076698]/40 to-transparent" />

        <div className="relative px-14 pb-16">
          <div className="w-10 h-0.5 bg-[#076698] mb-6 rounded-full" />
          <h1 className="text-4xl font-extrabold leading-tight tracking-tight text-slate-700 dark:text-slate-100 mb-4">
            Manage your<br />
            <span className="text-[#076698] dark:text-sky-400">platform.</span>
          </h1>
          <p className="text-sm leading-relaxed text-slate-500 dark:text-slate-400 max-w-xs">
            Oversee companies, candidates, and integrations with full control from a single admin studio.
          </p>
        </div>
      </div>

    </div>
  )
}
